import type { ReactNode } from 'react';
import type React from 'react';
import { toast } from 'react-toastify';
import type { FormikProps } from 'formik';
import { Formik } from 'formik';
import { Form, FormControl } from 'react-bootstrap';
import type { VendorUser, VendorUserSettingParam } from '~/models/VendorUser';
import { vendorUserValidationScheme } from '~/models/VendorUser';
import { Modal } from '~/components/common/parts/modal/Modal';
import { useVendorUsers } from '~/hooks/vendors/useVendorUsers';
import { create, update } from '~/utils/api/vendors/VendorUser';
import { isSuccess } from '~/utils/api/api_handler';
import { ModalFooter } from '~/components/common/parts/modal/ModalFooter';
import { FormGroup } from '~/components/common/parts/form/FormGroup';
import { FormLabel } from '~/components/common/parts/form/FormLabel';

type Props = {
  vendorUser?: VendorUser | null;
  handleClose: () => void;
};

export const VendorUserModal: React.FC<Props> = ({
  vendorUser = null,
  handleClose
}: Props) => {
  const { mutate: mutateVendorUsers } = useVendorUsers().index();
  const initialValue: VendorUserSettingParam = vendorUser ?? {
    name: '',
    tel: '',
    email: ''
  };
  const handleSave = async (
    submitValue: VendorUserSettingParam
  ): Promise<void> => {
    const result =
      vendorUser === null
        ? await create(submitValue)
        : await update(submitValue, vendorUser.id);
    if (isSuccess(result)) {
      await mutateVendorUsers?.();
      handleClose();
      toast.success('保存しました');
      return;
    }
    toast.error(result.message);
  };
  return (
    <Modal
      handleClose={handleClose}
      isOpen
      title={vendorUser === null ? '担当者追加' : '担当者編集'}
    >
      <Formik
        initialValues={initialValue}
        onSubmit={handleSave}
        validationSchema={vendorUserValidationScheme}
      >
        {({
          values,
          handleChange,
          handleBlur,
          errors,
          isSubmitting,
          handleSubmit,
          touched
        }: FormikProps<VendorUserSettingParam>): ReactNode => (
          <div>
            <FormGroup>
              <FormLabel>担当者名</FormLabel>
              <FormControl
                isInvalid={!!(touched.name && errors.name)}
                name="name"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.name ?? ''}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name}
              </Form.Control.Feedback>
            </FormGroup>
            <FormGroup>
              <FormLabel>電話番号</FormLabel>
              <FormControl
                isInvalid={!!(touched.tel && errors.tel)}
                name="tel"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.tel ?? ''}
              />
              <Form.Control.Feedback type="invalid">
                {errors.tel}
              </Form.Control.Feedback>
            </FormGroup>
            <FormGroup>
              <FormLabel>メールアドレス</FormLabel>
              <FormControl
                isInvalid={!!(touched.email && errors.email)}
                name="email"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.email ?? ''}
              />
              <Form.Control.Feedback type="invalid">
                {errors.email}
              </Form.Control.Feedback>
            </FormGroup>
            <ModalFooter
              isSubmitting={isSubmitting}
              onClickAccept={handleSubmit}
              onClickCancel={handleClose}
            />
          </div>
        )}
      </Formik>
    </Modal>
  );
};
